import { useEffect, useContext, useState, useCallback } from 'react';
import Balls from '../components/Balls';
import { AppContext, Env } from '../context';
import { Locale } from '../lib/localisation';
import { completePayment } from '../lib/paymentService';
import { getParameterByName, PARAMS, wait, isWebView } from '../lib/util';
import './CompleteThreeDS.css';

export default function CompleteThreeDS(): JSX.Element {
  const context = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const [interceptUrl, setInterceptUrl] = useState<string | null>(null);

  const parseDefaultErrorMessage = useCallback(
    (msg?: string) =>
      context.locale === Locale.EN
        ? `Uhoh Something went wrong: ${msg}`
        : `Uffda Noe gikk galt: ${msg}`,
    [context.locale]
  );

  const redirect = useCallback((redirectUrl: string | null) => {
    if (!isWebView()) {
      const queryParams = redirectUrl?.substring(redirectUrl.indexOf('?'));
      const customScheme =
        context.env === Env.UAT ? 'test-vippsmobilepaycardpsp://' : 'vippsmobilepaycardpsp://';
      const deeplink = customScheme + queryParams;
      window.location.replace(deeplink);
    } else {
      redirectUrl && window.location.replace(redirectUrl);
    }
  }, []);

  const sendComplete3dsRequest = useCallback(async () => {
    setLoading(true);
    const data = {
      // Only sent by Adyen Checkout API v66:
      MD: getParameterByName(PARAMS.MD) || undefined,
      PaRes: getParameterByName(PARAMS.PARES) || undefined,
      // Only sent by Adyen Checkout API v67:
      redirectResult: getParameterByName(PARAMS.REDIRECT_RESULT) || undefined,
    };
    try {
      const [err, response] = await Promise.all([wait(1000), completePayment(context, data)]).then(
        ([, v]) => v
      );
      setComplete(true);
      setLoading(false);
      if (err) {
        setError(parseDefaultErrorMessage(err?.message));
        return;
      }
      setInterceptUrl(response?.interceptUrl as string);
      if (response?.adyenData.resultCode === 'Refused') {
        const message = response.message;
        setError(message && message.length > 0 ? message : parseDefaultErrorMessage());
        return;
      }
      response && response.interceptUrl && redirect(response.interceptUrl);
    } catch (e) {
      setError(parseDefaultErrorMessage((e as Error)?.message));
    } finally {
      setLoading(false);
    }
  }, [context, parseDefaultErrorMessage]);

  useEffect(() => {
    if (complete || loading) {
      return;
    }
    sendComplete3dsRequest();
  }, [complete, sendComplete3dsRequest, loading]);

  if (complete && error?.length === 0) {
    return (
      <div className="threeds">
        <div className="close-button-group">
          <button type="button" onClick={() => redirect(interceptUrl)}>
            {context.texts.closeButton}
          </button>
        </div>
      </div>
    );
  } else if (complete && error?.length !== 0) {
    return (
      <div className="threeds">
        <div className="error-container" role="alert">
          <p>{error}</p>
        </div>
        <div className="splitter"></div>
        <div className="close-button-group">
          <button type="button" onClick={() => redirect(interceptUrl)}>
            {context.texts.closeButton}
          </button>
        </div>
      </div>
    );
  } else {
    return <Balls />;
  }
}
